/**
 * Local, non-cryptographic-purpose identifiers: outbox row ids and blob ids.
 *
 * Kept separate from `idempotency.ts` on purpose. An idempotency key is part of an API CONTRACT —
 * it is canonicalised, charset-constrained and echoed by the server — whereas these two ids never
 * leave the device. Sharing one generator would invite someone to "simplify" by using an outbox row
 * id as the idempotency key, and then the two lifetimes (a local row that can be deleted, a
 * server-side dedupe key that must be stable forever) would be welded together.
 */

function randomHex(byteCount: number): string {
  const cryptoObj: Crypto | undefined = globalThis.crypto;
  if (cryptoObj === undefined || typeof cryptoObj.getRandomValues !== 'function') {
    throw new Error('no CSPRNG available');
  }
  const bytes = cryptoObj.getRandomValues(new Uint8Array(byteCount));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * An outbox row id: `<ms timestamp>-<sequence>-<random>`, lexicographically sortable.
 *
 * Time-prefixed so the store's natural key order is creation order — which makes the queue drain
 * oldest-first without a sort, and makes a dump of the store readable when someone is working out
 * what a device was doing.
 *
 * **THE SEQUENCE COUNTER IS NOT DECORATION.** A first version of this used timestamp + randomness
 * only, and `test/outbox-durability.test.ts` caught it immediately: two `enqueue` calls in the same
 * millisecond share the timestamp, so the RANDOM suffix decided their order and the queue came back
 * reversed. The counter makes same-millisecond ordering deterministic.
 *
 * ⚠️ RESIDUAL LIMITATION, stated rather than hidden: the counter is per process, so it restarts at
 * zero on reload. Two items created in the SAME millisecond either side of a reload can still order
 * arbitrarily relative to each other. Nothing depends on ordering for correctness — the flusher
 * attempts every due item, and ordering is a "the first thing I submitted lands first" nicety — but
 * it is a real edge and it should not be described as solved.
 *
 * The random suffix stays, because two devices are irrelevant here but two *tabs* are not: they run
 * separate module instances with separate counters against one database.
 */
let sequence = 0;

export function newOutboxId(): string {
  sequence += 1;
  const stamp = Date.now().toString(36).padStart(9, '0');
  const seq = (sequence % 36 ** 4).toString(36).padStart(4, '0');
  return `${stamp}-${seq}-${randomHex(8)}`;
}

export function newBlobId(): string {
  return `blob-${randomHex(16)}`;
}
